import React, { useEffect, useState } from "react";
import { Sparkles, Check } from "lucide-react";
import api from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { formatAUD } from "@/lib/media";
import { toast } from "sonner";

const Chip = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-2.5 py-1 rounded-full text-xs font-display font-semibold border transition-colors ${active ? "bg-[#19324A] text-white border-[#19324A]" : "bg-white text-[#19324A]/70 border-[#19324A]/15 hover:border-[#FF6B9D]"}`}>
    {children}
  </button>
);

// Side-cart nudge: only the book is in the bag → offer to upgrade it to the Book + Tee bundle (swaps the book out).
export default function CartBundleNudge() {
  const { cart, addBundle, removeFromCart, updateQty } = useStore();
  const [cfg, setCfg] = useState(null);
  const [size, setSize] = useState(null);
  const [colour, setColour] = useState(null);

  useEffect(() => { api.get("/upsell/book-tee").then(({ data }) => setCfg(data)).catch(() => setCfg({ active: false })); }, []);
  if (!cfg?.active) return null;

  const bookLine = cart.find((i) => i.type !== "bundle" && String(i.id) === String(cfg.book_product_id));
  const hasTee = cart.some((i) => i.type !== "bundle" && String(i.id) === String(cfg.tee_product_id));
  const hasBundle = cart.some((i) => i.type === "bundle" && i.kind === "book-tee");
  if (!bookLine || hasTee || hasBundle || !(cfg.saving > 0)) return null;

  const { book, tee } = cfg;
  const needSize = tee.sizes?.length > 0, needColour = tee.colours?.length > 0;
  const ready = (!needSize || size) && (!needColour || colour);

  const upgrade = () => {
    if (!ready) { toast.error(`Pick a ${needColour && !colour ? "colour" : "size"} for the tee first.`); return; }
    if (bookLine.quantity > 1) updateQty(bookLine.key, bookLine.quantity - 1); else removeFromCart(bookLine.key);
    addBundle({
      kind: "book-tee", name: cfg.name, price: cfg.price, image: book.image,
      components: [{ product_id: book.id, name: book.name }, { product_id: tee.id, name: tee.name, size, colour }],
    });
    toast.success(`Upgraded to the bundle — you saved ${formatAUD(cfg.saving)} 🐾`);
  };

  return (
    <div className="rounded-2xl border-2 border-dashed border-[#FFD93D] bg-white p-3" data-testid="cart-bundle-nudge">
      <div className="flex gap-3">
        <img src={tee.image} alt={tee.name} className="w-16 h-16 rounded-xl object-cover bg-[#FFF9EE]" />
        <div className="flex-1 min-w-0">
          <p className="font-hand text-[#FF6B9D] text-base leading-none">Make it a matching set</p>
          <p className="font-display font-semibold text-[#19324A] text-sm leading-snug mt-1">Add the {tee.name} and save <span data-testid="cart-bundle-saving">{formatAUD(cfg.saving)}</span></p>
          <p className="text-xs text-[#19324A]/55 mt-0.5">Bundle {formatAUD(cfg.price)} <span className="line-through">{formatAUD(cfg.separate_total)}</span></p>
        </div>
      </div>
      {needColour && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {tee.colours.map((c) => <Chip key={c} active={colour === c} onClick={() => setColour(c)} testid={`cart-bundle-colour-${c}`}>{c}</Chip>)}
        </div>
      )}
      {needSize && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {tee.sizes.map((s) => <Chip key={s} active={size === s} onClick={() => setSize(s)} testid={`cart-bundle-size-${s}`}>{s}</Chip>)}
        </div>
      )}
      <button type="button" onClick={upgrade} data-testid="cart-bundle-add" className={`mt-3 w-full btn-pink !py-2 text-sm ${ready ? "" : "opacity-70"}`}>
        {ready ? <Check className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />} Upgrade to the bundle — {formatAUD(cfg.price)}
      </button>
    </div>
  );
}
